import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { 
  Sparkles, Calendar, Users, Video, Tag, Trophy, 
  MessageSquare, BarChart3, Award, ChevronRight,
  X, Check
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";

interface Feature {
  id: string;
  name: string;
  description: string;
  icon: React.ElementType;
  path: string;
  category: 'contenido' | 'comunidad' | 'negocio';
}

const FEATURES: Feature[] = [
  {
    id: 'tribus',
    name: 'Tribus',
    description: 'Crea comunidades fitness y desafíos grupales',
    icon: Users,
    path: '/tribus',
    category: 'comunidad',
  },
  {
    id: 'eventos',
    name: 'Eventos',
    description: 'Publica y promociona eventos deportivos',
    icon: Calendar,
    path: '/events',
    category: 'contenido',
  },
  {
    id: 'videos',
    name: 'Videos',
    description: 'Comparte contenido de video con tu audiencia',
    icon: Video,
    path: '/videos',
    category: 'contenido',
  },
  {
    id: 'cupones',
    name: 'Cupones',
    description: 'Ofrece descuentos y promociones exclusivas',
    icon: Tag,
    path: '/coupons',
    category: 'negocio',
  },
  {
    id: 'desafios',
    name: 'Desafíos',
    description: 'Crea retos fitness con premios y rankings',
    icon: Trophy,
    path: '/desafios',
    category: 'comunidad',
  },
  {
    id: 'chat-ai',
    name: 'Asistente IA',
    description: 'Obtén ayuda personalizada de Alex, tu guía',
    icon: MessageSquare,
    path: '/chat-assistant',
    category: 'negocio',
  },
  {
    id: 'afiliados',
    name: 'Programa de Afiliados',
    description: 'Gana comisiones recomendando la plataforma',
    icon: Award,
    path: '/affiliate/dashboard',
    category: 'negocio',
  },
];

const STORAGE_KEY = 'ekselencia_features_discovered';

interface DescubrimientoCaracteristicasProps {
  className?: string;
}

export function DescubrimientoCaracteristicas({ className }: DescubrimientoCaracteristicasProps) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [discoveredFeatures, setDiscoveredFeatures] = useState<string[]>([]);
  const [showPulse, setShowPulse] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        setDiscoveredFeatures(JSON.parse(stored));
      } catch {
        setDiscoveredFeatures([]);
      }
    }
  }, []);

  useEffect(() => {
    const undiscoveredCount = FEATURES.length - discoveredFeatures.length;
    setShowPulse(undiscoveredCount > 0 && discoveredFeatures.length < 3);
  }, [discoveredFeatures]);

  const markAsDiscovered = (featureId: string) => {
    if (!discoveredFeatures.includes(featureId)) {
      const updated = [...discoveredFeatures, featureId];
      setDiscoveredFeatures(updated);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    }
  };

  const handleFeatureClick = (feature: Feature) => {
    markAsDiscovered(feature.id);
    navigate(feature.path);
    setIsOpen(false);
  };

  const undiscoveredCount = FEATURES.length - discoveredFeatures.length;

  const categories = [
    { id: 'contenido', label: 'Contenido' },
    { id: 'comunidad', label: 'Comunidad' },
    { id: 'negocio', label: 'Negocio' },
  ];

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            "relative gap-2",
            showPulse && "animate-pulse",
            className
          )}
        >
          <Sparkles className="h-4 w-4 text-primary" />
          Descubrir
          {undiscoveredCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center text-xs"
            >
              {undiscoveredCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-80 p-0" 
        align="end"
        sideOffset={8}
      >
        <div className="p-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              <h3 className="font-semibold">Descubre Ekselencia</h3>
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-6 w-6"
              onClick={() => setIsOpen(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Explora todas las funcionalidades disponibles
          </p>
        </div>

        <div className="max-h-80 overflow-y-auto">
          {categories.map((category) => (
            <div key={category.id} className="p-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase px-2 mb-2">
                {category.label}
              </p>
              {FEATURES.filter(f => f.category === category.id).map((feature) => {
                const isDiscovered = discoveredFeatures.includes(feature.id);
                const Icon = feature.icon;
                
                return (
                  <button
                    key={feature.id}
                    onClick={() => handleFeatureClick(feature)}
                    className={cn(
                      "w-full flex items-center gap-3 p-2 rounded-lg text-left transition-colors",
                      "hover:bg-accent",
                      isDiscovered ? "opacity-60" : "bg-primary/5"
                    )}
                  >
                    <div className={cn(
                      "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center",
                      isDiscovered ? "bg-muted" : "bg-primary/10"
                    )}>
                      {isDiscovered ? (
                        <Check className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Icon className="h-4 w-4 text-primary" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{feature.name}</span>
                        {!isDiscovered && (
                          <Badge variant="secondary" className="text-[10px] px-1 py-0">
                            Nuevo
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {feature.description}
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {undiscoveredCount === 0 && (
          <div className="p-4 border-t bg-primary/5 text-center">
            <p className="text-sm font-medium text-primary">
              🎉 ¡Has explorado todas las funcionalidades!
            </p>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
